import { useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
} from '@/shared/ui';
import { UI_TEXT } from '@/shared/languages';
import { useNavigator } from '@/shared/navigation';
import { ROUTES } from '@/shared/routing';
import { useVersionCheckQuery } from '@/shared/version';

/**
 * 버전 업데이트 게이트 — 구버전 사용자를 플랫폼별로 다른 표면에 태운다.
 *
 * | platform | 대상 status              | 처리                                    |
 * | -------- | ------------------------ | --------------------------------------- |
 * | `web`    | `up-to-date` 아닌 전부   | 이 다이얼로그(닫으면 새로고침)          |
 * | `native` | `update-required` 만     | `/update-required` 전용 페이지로 replace |
 *
 * native 는 **양방향**이다 — 판정이 `update-required` 를 벗어나면 그 화면에서 본체로 다시 replace 한다.
 * 그 화면에는 스토어 버튼 말고 출구가 없어서, 게이트가 안 풀어주면 갇힌다(아래 effect 참조).
 *
 * 기준이 갈리는 이유는 **해소 비용**이다. 웹은 새로고침 한 번이면 새 SPA 자산을 받아 넛지(latest)·강제
 * (minimum)가 모두 풀리니 구분 없이 띄워도 부담이 없다. native 는 스토어를 거쳐야 해서 즉시 해소가 불가능하므로,
 * 아직 정상 동작하는 넛지(`update-available`) 단계까지 화면을 뺏지 않고 **강제(minimum 미만)만** 격리한다.
 *
 * 닫기(X) 버튼 없음(heritage `AlertDialog` 기본) + **어떤 방식으로 닫아도 새로고침**(ESC·바깥 등 onOpenChange
 * → reload / 버튼 → reload) — 구버전 자산으로 계속 쓰는 걸 막는다. 조회 실패·로딩은 무시(fail-open) — 게이트가
 * 앱 진입을 막아선 안 된다.
 *
 * 열림 자체는 reload 를 유발하지 않는다(사용자 액션 시에만) — 자산이 아직 안 올라온 순간에도 무한 새로고침 없음.
 *
 * # 관측(콘솔)도 여기가 소유한다
 * 이 게이트는 **떠도 안 떠도 화면에 흔적이 안 남는 구간**이 있다 — native 는 `platform!=='web'` 이라 절대
 * 열리지 않고, 조회 실패는 fail-open 이라 조용히 지나간다. 그래서 "버전 체크가 돌긴 하는가"를 볼 유일한
 * 창구로 결과·실패를 콘솔에 남긴다. 특히 native 는 bridge `version.fetch` 로 CORS 를 우회하는 경로라
 * 기기에서 한 번은 눈으로 확인해야 한다. **prod 에서도 남긴다** — 운영에서 매니페스트가 실제로 읽히는지
 * 검증이 필요해 환경 게이트를 두지 않는다.
 *
 * (composition 이 같은 query 를 `QueryObserver` 로 따로 구독해 로깅하던 프로브를 여기로 흡수했다. 그 프로브의
 * 명분이던 "활성 observer 유지 → `refetchOnWindowFocus` 성립"은 항상 마운트되는 이 위젯이 이미 충족한다.)
 */
export function VersionUpdateGate() {
  const { data, error } = useVersionCheckQuery();
  const navigator = useNavigator();
  const open = data?.platform === 'web' && data.status !== 'up-to-date';
  // native 는 **강제(minimum 미만)만** 격리한다 — 넛지(`update-available`)까지 화면을 뺏으면 아직 정상
  // 동작하는 앱을 못 쓰게 만든다. web 과 달리 즉시 해소할 수단(새로고침)이 없어 기준이 더 보수적이다.
  const blockedNative = data?.platform === 'native' && data.status === 'update-required';
  // 차단 해소 — 판정이 나왔고(`data` 존재) 더는 강제 대상이 아님. `!blockedNative` 와 다르다(로딩·실패 제외).
  const clearedNative = data?.platform === 'native' && data.status !== 'update-required';

  useEffect(() => {
    // `replace` — 히스토리에 직전 화면을 남기지 않는다(뒤로가기로 구버전 본체 복귀 차단).
    // 자기 자신으로의 이동은 navigator 가 self-loop 로 걸러낸다.
    if (blockedNative) navigator.replace(ROUTES.UPDATE_REQUIRED);
  }, [blockedNative, navigator]);

  useEffect(() => {
    // 해소 복귀(차단의 역방향) — 판정이 `update-required` 를 벗어났는데 화면에 남아 있으면 앱을 강제 종료하는
    // 것 말곤 탈출구가 없다(페이지에 스토어 버튼 외 출구를 두지 않으므로). 스토어 업데이트는 앱 재시작이라
    // 루트부터 다시 뜨지만, **매니페스트 정정**(minimum 을 잘못 올렸다 되돌린 경우)은 세션 유지 상태로
    // 포커스 refetch 에서 풀려 이 경로가 유일한 복구다. `isVersionBlocked()`(BE 통신 차단)는 이미 캐시
    // 동기 조회라 자동 해소되는데 화면만 안 풀리는 비대칭도 여기서 닫는다.
    //
    // 조건이 `!blockedNative` 가 아니라 **판정이 실제로 나온 경우(`clearedNative`)만**인 건 fail-open 대칭 —
    // 로딩·조회 실패로는 화면을 벗기지 않는다(차단을 걸 때 확인이 필요했듯 풀 때도 확인이 필요하다).
    // 위치 판정은 `window.location` — 이 위젯은 라우터 트리 **밖**(AppProvider)이라 `useLocation` 이 없다.
    // BrowserRouter 라 pathname 은 실주소와 일치한다.
    if (clearedNative && window.location.pathname === ROUTES.UPDATE_REQUIRED) {
      navigator.replace(ROUTES.DASHBOARD);
    }
  }, [clearedNative, navigator]);

  useEffect(() => {
    // 원인 구분용(미배포 404 · CORS · 구버전 앱의 unknown message type · 타임아웃). 앱 사용은 막지 않는다.
    if (error) console.warn('[version] check 실패 (fail-open)', error);
  }, [error]);

  const refresh = () => {
    window.location.reload();
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) refresh(); // 닫힘(ESC·프로그램적 dismiss)도 새로고침으로 귀결.
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{UI_TEXT.APP_UPDATE_TITLE}</AlertDialogTitle>
          <AlertDialogDescription>{UI_TEXT.APP_UPDATE_DESC}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button onClick={refresh}>{UI_TEXT.APP_UPDATE_REFRESH}</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
